/**
 * =============================================================================
 * GLUCERA — FLUTTER WEBVIEW BRIDGE CONTRACT (single reference file)
 * =============================================================================
 *
 * Web implementation: ../utils/flutterHealthBridge.ts
 * Shared web callbacks: ../utils/nativeHealthBridge.ts (window.GluceraWeb)
 * Types:              ../types/flutterHealthBridge.types.ts
 * Trends Sync UI:      ../hooks/useGoogleHealthSync.ts
 *
 * Share this file with the Flutter team.
 *
 * Package: flutter_inappwebview
 *
 * -----------------------------------------------------------------------------
 * FLOW (login on web — primary Glucera flow)
 * -----------------------------------------------------------------------------
 * 1. User logs in inside WebView (Google login on web)
 * 2. Web sets cookies: glucera_access_token, glucera_user_id, glucera_user_info
 * 3. Flutter reads auth via evaluateJavascript:
 *      window.GluceraWeb.getAuthContext()
 *    OR read cookies from CookieManager
 * 4. User opens Trends → taps Sync
 * 5. Web calls: flutter_inappwebview.callHandler('requestGoogleHealthData')
 * 6. Flutter reads Google Health Connect → calls:
 *      window.GluceraWeb.onHealthData(json)
 * 7. Web POSTs health_data to /food_scanner/health-sync/
 * 8. Web GETs /food_scanner/health-sync/ and updates Trends UI
 *
 * -----------------------------------------------------------------------------
 * FLOW (login on Flutter — optional)
 * -----------------------------------------------------------------------------
 * 1. User logs in natively in Flutter app
 * 2. Web calls: flutter_inappwebview.callHandler('getUserData')
 * 3. Flutter returns { userId, accessToken, refreshToken?, userInfo? }
 * 4. Web saves session (tryInjectAuthFromFlutter on app load)
 *
 * -----------------------------------------------------------------------------
 * FLUTTER SETUP (required)
 * -----------------------------------------------------------------------------
 *
 * InAppWebView(
 *   initialSettings: InAppWebViewSettings(
 *     javaScriptEnabled: true,
 *     domStorageEnabled: true,  // required for web login / localStorage
 *   ),
 *   onWebViewCreated: (controller) {
 *     // Handler: web asks Flutter for auth (optional native-login flow)
 *     controller.addJavaScriptHandler(
 *       handlerName: 'getUserData',
 *       callback: (args) {
 *         return {
 *           'userId': currentUserId,
 *           'accessToken': accessToken,
 *           'refreshToken': refreshToken,
 *           'userInfo': userInfoMap,  // see USER_INFO_JSON_EXAMPLE
 *         };
 *       },
 *     );
 *
 *     // Handler: Trends Sync button on web
 *     controller.addJavaScriptHandler(
 *       handlerName: 'requestGoogleHealthData',
 *       callback: (args) async {
 *         try {
 *           final json = await buildHealthJsonFromGoogleHealth();
 *           await controller.evaluateJavascript(
 *             source: "window.GluceraWeb.onHealthData($json);",
 *           );
 *         } catch (e) {
 *           await controller.evaluateJavascript(
 *             source: "window.GluceraWeb.onHealthDataError('${e.toString()}');",
 *           );
 *         }
 *       },
 *     );
 *   },
 * )
 *
 * -----------------------------------------------------------------------------
 * WEB → FLUTTER (handlers Flutter must register)
 * -----------------------------------------------------------------------------
 *
 *   getUserData              → returns auth JSON (optional)
 *   requestGoogleHealthData  → reads health, calls GluceraWeb.onHealthData
 *   pressLogout              → Flutter clears WebView cookies + storage; web also clears localStorage
 *
 * -----------------------------------------------------------------------------
 * FLUTTER → WEB (already implemented on web — evaluateJavascript)
 * -----------------------------------------------------------------------------
 *
 *   window.GluceraWeb.getAuthContext()
 *     → '{"userId":"uuid","accessToken":"jwt..."}'
 *
 *   window.GluceraWeb.onHealthData(payload)
 *   window.GluceraWeb.onHealthDataError(message)
 *
 * -----------------------------------------------------------------------------
 * AUTH COOKIES (web login — read via CookieManager.getCookie)
 * -----------------------------------------------------------------------------
 *   glucera_access_token
 *   glucera_user_id
 *   glucera_user_info     — full user JSON (URL-encoded)
 *
 * =============================================================================
 */

/** Handler names — must match addJavaScriptHandler on Flutter side. */
export const FLUTTER_HANDLER_GET_USER_DATA = "getUserData" as const;
export const FLUTTER_HANDLER_REQUEST_HEALTH = "requestGoogleHealthData" as const;
export const FLUTTER_HANDLER_PRESS_LOGOUT = "pressLogout" as const;

/** Shared web callback object (same as Android bridge). */
export const WEB_BRIDGE_OBJECT_NAME = "GluceraWeb" as const;
export const WEB_BRIDGE_GET_AUTH_CONTEXT = "getAuthContext" as const;
export const WEB_BRIDGE_ON_HEALTH_DATA = "onHealthData" as const;
export const WEB_BRIDGE_ON_HEALTH_DATA_ERROR = "onHealthDataError" as const;

/** Cookies set after web login. */
export const COOKIE_ACCESS_TOKEN = "glucera_access_token" as const;
export const COOKIE_USER_ID = "glucera_user_id" as const;
export const COOKIE_USER_INFO = "glucera_user_info" as const;

/** Example getUserData response (Flutter → web). */
export const FLUTTER_USER_DATA_EXAMPLE = {
  userId: "63773960-4e82-4850-929a-70c8d0b225b6",
  accessToken: "eyJhbGciOiJIUzI1NiIs...",
  refreshToken: "eyJhbGciOiJIUzI1NiIs...",
  userInfo: {
    id: "63773960-4e82-4850-929a-70c8d0b225b6",
    username: "nidhi.universalsoftware_NT",
    email: "nidhi.universalsoftware@gmail.com",
    full_name: "Hr Adminnnb",
    first_name: "Hr",
    last_name: "Admin",
    gender: "",
    date_of_birth: null as string | null,
    is_email_verified: true,
    user_type: "tenant_user",
  },
} as const;

/** Re-use health payload shape from Android contract. */
export { HEALTH_DATA_JSON_EXAMPLE } from "./androidWebViewBridge.contract";

/** Flutter reads auth after web login. */
export const FLUTTER_GET_AUTH_JS = `
final authJson = await webViewController.evaluateJavascript(
  source: "window.GluceraWeb.getAuthContext()",
);
// {"userId":"...","accessToken":"..."}
`.trim();

/** Web reads auth from Flutter (native-login flow). */
export const FLUTTER_GET_USER_DATA_JS = `
const data = await window.flutter_inappwebview.callHandler('getUserData');
// { userId, accessToken, refreshToken?, userInfo? }
`.trim();

/** Web triggers health sync (Trends Sync button). */
export const FLUTTER_REQUEST_HEALTH_JS = `
await window.flutter_inappwebview.callHandler('requestGoogleHealthData');
`.trim();

/** Web triggers logout (Profile page). */
export const FLUTTER_PRESS_LOGOUT_JS = `
await window.flutter_inappwebview.callHandler('pressLogout');
`.trim();

export type { FlutterUserData } from "../types/flutterHealthBridge.types";
